import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CookieConsent from './CookieConsent';

const {FiMail,FiLinkedin,FiTwitter,FiGithub,FiArrowUp,FiHeart} = FiIcons;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  return (
    <>
      <CookieConsent />
      <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-forest-900 text-slate-200 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-forest-600/5 via-forest-500/5 to-forest-400/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <motion.div
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                viewport={{once: true}}
                className="mb-8"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <motion.div
                    className="w-12 h-12 bg-gradient-to-br from-forest-600 to-forest-700 rounded-xl flex items-center justify-center shadow-lg"
                    whileHover={{scale: 1.1, rotate: 5}}
                  >
                    <span className="text-white font-bold text-xl">A</span>
                  </motion.div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-forest-400 to-forest-300 bg-clip-text text-transparent">
                    aiximo
                  </span>
                </div>

                <p className="text-slate-400 leading-relaxed mb-8 max-w-md">
                  Unlock rapid, high-quality, and cost-effective software development with our AI-powered solutions. Empowering startups, SMBs, and enterprises to innovate and scale.
                </p>

                <div className="flex space-x-4">
                  {[
                    {icon: FiMail, href: 'mailto:info@aiximo.com', label: 'Email'},
                    {icon: FiLinkedin, href: '#', label: 'LinkedIn'},
                    {icon: FiTwitter, href: '#', label: 'Twitter'},
                    {icon: FiGithub, href: '#', label: 'GitHub'}
                  ].map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      whileHover={{scale: 1.1, y: -2}}
                      whileTap={{scale: 0.95}}
                      className="w-12 h-12 bg-gray-800/50 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-gradient-to-br hover:from-forest-600 hover:to-forest-700 transition-all duration-300 border border-gray-700/50 hover:border-transparent"
                      aria-label={social.label}
                    >
                      <SafeIcon icon={social.icon} className="h-5 w-5" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Services */}
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: 0.1}}
            >
              <h3 className="text-lg font-semibold mb-6 bg-gradient-to-r from-forest-400 to-forest-300 bg-clip-text text-transparent">
                Services
              </h3>
              <ul className="space-y-3">
                {[
                  'Rapid App Development',
                  'AI Code Generation',
                  'Prompt Engineering',
                  'Code Auditing',
                  'Maintenance & Support'
                ].map((service, index) => (
                  <motion.li key={index} whileHover={{x: 5}}>
                    <Link
                      to="/services"
                      className="text-slate-400 hover:text-white transition-all duration-300 flex items-center space-x-2 group"
                    >
                      <div className="w-1 h-1 bg-gradient-to-r from-forest-400 to-forest-300 rounded-full group-hover:w-2 transition-all duration-300"></div>
                      <span>{service}</span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Company */}
            <motion.div
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{delay: 0.2}}
            >
              <h3 className="text-lg font-semibold mb-6 bg-gradient-to-r from-forest-400 to-forest-300 bg-clip-text text-transparent">
                Company
              </h3>
              <ul className="space-y-3">
                {[
                  {name: 'About Us', path: '/about'},
                  {name: 'Case Studies', path: '/case-studies'},
                  {name: 'Blog', path: '/blog'},
                  {name: 'Contact', path: '/contact'}
                ].map((item, index) => (
                  <motion.li key={index} whileHover={{x: 5}}>
                    <Link
                      to={item.path}
                      className="text-slate-400 hover:text-white transition-all duration-300 flex items-center space-x-2 group"
                    >
                      <div className="w-1 h-1 bg-gradient-to-r from-forest-400 to-forest-300 rounded-full group-hover:w-2 transition-all duration-300"></div>
                      <span>{item.name}</span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{opacity: 0, y: 20}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{delay: 0.3}}
            className="border-t border-gray-700/50 mt-12 pt-8"
          >
            {/* Legal Links */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <div className="flex items-center space-x-2 text-slate-400 text-sm mb-4 md:mb-0">
                <span>© 2024 aiximo. Made with</span>
                <motion.div
                  animate={{scale: [1, 1.2, 1]}}
                  transition={{duration: 1, repeat: Infinity, repeatDelay: 2}}
                >
                  <SafeIcon icon={FiHeart} className="h-4 w-4 text-red-500" />
                </motion.div>
                <span>All rights reserved.</span>
              </div>

              <div className="flex items-center space-x-6">
                <Link
                  to="/privacy-policy"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms-of-service"
                  className="text-slate-400 hover:text-white text-sm transition-colors"
                >
                  Terms of Service
                </Link>
                {/* Only show cookie settings button in footer if needed */}
                <CookieConsent showSettingsButton={true} />
              </div>
            </div>

            {/* Scroll to Top */}
            <div className="flex justify-center">
              <motion.button
                onClick={scrollToTop}
                whileHover={{scale: 1.1, y: -2}}
                whileTap={{scale: 0.95}}
                className="w-10 h-10 bg-gradient-to-br from-forest-600 to-forest-700 rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300"
                aria-label="Scroll to top"
              >
                <SafeIcon icon={FiArrowUp} className="h-5 w-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </footer>
    </>
  );
};

export default Footer;